import React, { useCallback, useEffect, useState } from "react";
import { Plus, Calendar, Clock, Trash2, X, CheckCircle2, User, Sparkles, MessageCircle, Video, Eye, List, LayoutGrid } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import AgendaCalendar from "@/components/AgendaCalendar";

const formatWhen = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "en" ? "en-US" : "es-CO", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// Nice-to-have local presets for the datetime picker
const nowLocal = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15);
  return d.toISOString().slice(0, 16);
};
const addHoursLocal = (hours) => {
  const d = new Date();
  d.setHours(d.getHours() + hours, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

const AvailabilityManager = () => {
  const { t, lang } = useI18n();
  const r = t.redApoyo;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("calendar"); // "list" | "calendar"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/slots/mine");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ start_at: "", duration_min: 30, topic_hint: "" });
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true); setError("");
    try {
      // Convert local datetime-local input to ISO
      const iso = new Date(editing.start_at).toISOString();
      await api.post("/appointments/slots", {
        start_at: iso,
        duration_min: Number(editing.duration_min) || 30,
        topic_hint: (editing.topic_hint || "").trim(),
      });
      await load();
      close();
    } catch (err) {
      setError(formatApiError(err));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(r.removeBtn + "?")) return;
    try { await api.delete(`/appointments/slots/${id}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  return (
    <section className="mt-10 pt-8 border-t border-gray-100" data-testid="availability-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow text-orange">{r.navLabel}</p>
          <h2 className="font-display text-3xl text-teal-deep mt-1 leading-tight">{r.availabilityTitle}</h2>
          <p className="text-sm text-teal-soft mt-2 max-w-xl">{r.availabilitySub}</p>
        </div>
        <button type="button" onClick={openNew} className="btn-orange" data-testid="add-slot-btn">
          <Plus size={14} /> {r.addSlotBtn}
        </button>
      </div>

      {error && <p className="text-red-600 text-sm mt-3" data-testid="availability-error">{error}</p>}

      {/* View toggle: Calendar vs List */}
      {!loading && items.length > 0 && (
        <div className="mt-4 inline-flex bg-cream rounded-xl p-1 gap-1" data-testid="avail-view-toggle">
          <button
            type="button"
            onClick={() => setMode("calendar")}
            className={`text-xs font-bold uppercase tracking-wider rounded-lg px-3 py-2 inline-flex items-center gap-1.5 transition-colors ${
              mode === "calendar" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"
            }`}
            data-testid="avail-view-calendar"
          >
            <LayoutGrid size={13} /> Calendario
          </button>
          <button
            type="button"
            onClick={() => setMode("list")}
            className={`text-xs font-bold uppercase tracking-wider rounded-lg px-3 py-2 inline-flex items-center gap-1.5 transition-colors ${
              mode === "list" ? "bg-white text-teal-deep shadow-sm" : "text-teal-soft hover:text-teal-deep"
            }`}
            data-testid="avail-view-list"
          >
            <List size={13} /> Lista
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-teal-soft text-sm mt-6">…</p>
      ) : items.length === 0 ? (
        <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="availability-empty">
          <div className="p-6 md:p-8 bg-gradient-to-br from-cream to-white">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 bg-orange/10 text-orange text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md">
                <Sparkles size={12} /> {r.emptyHowItWorks || "Así funciona"}
              </span>
              <h3 className="font-display text-2xl md:text-3xl text-teal-deep leading-tight mt-3">
                {r.emptyTitle || "Abre tus primeros horarios en 3 pasos"}
              </h3>
              <p className="text-teal-soft text-sm mt-2">
                {r.emptySub || "Los pacientes de la comunidad podrán agendar directamente en los horarios que tú publiques."}
              </p>
            </div>

            <div className="mt-6 grid md:grid-cols-3 gap-3">
              {[
                { Icon: Calendar, title: r.emptyStep1Title || "1. Publica un horario", desc: r.emptyStep1Desc || "Elige fecha, hora y duración (15 a 120 min)." },
                { Icon: MessageCircle, title: r.emptyStep2Title || "2. Recibe la reserva", desc: r.emptyStep2Desc || "Un paciente reserva y verás su nombre y motivo aquí." },
                { Icon: Video, title: r.emptyStep3Title || "3. Atiende la cita", desc: r.emptyStep3Desc || "Contacta al paciente en el horario acordado (Zoom, teléfono, etc)." },
              ].map(({ Icon, title, desc }, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                  <span className="w-9 h-9 rounded-lg bg-orange/10 text-orange flex items-center justify-center">
                    <Icon size={16} />
                  </span>
                  <h4 className="font-display text-teal-deep mt-3 text-base leading-tight">{title}</h4>
                  <p className="text-xs text-teal-soft mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3 items-center">
              <button type="button" onClick={openNew} className="btn-orange text-base px-6 py-3.5" data-testid="empty-add-slot-btn">
                <Plus size={16} /> {r.emptyAddFirst || "Agregar mi primer horario"}
              </button>
              <p className="text-xs text-teal-soft inline-flex items-center gap-1.5">
                <Eye size={12} /> {r.emptyPreviewHint || "Los pacientes verán tu agenda en la página RED Online."}
              </p>
            </div>
          </div>
        </div>
      ) : mode === "calendar" ? (
        <AgendaCalendar items={items} />
      ) : (
        <ul className="mt-6 space-y-3">
          {items.map((s) => {
            const isBooked = s.status === "booked";
            return (
              <li key={s.id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-start gap-3" data-testid={`avail-${s.id}`}>
                <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isBooked ? "bg-teal text-white" : "bg-orange/15 text-orange"}`}>
                  {isBooked ? <CheckCircle2 size={16} /> : <Calendar size={16} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg text-teal-deep">{formatWhen(s.start_at, lang)}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${isBooked ? "bg-teal text-white" : "bg-orange/15 text-orange"}`}>
                      {isBooked ? r.statusBooked : r.statusOpen}
                    </span>
                  </div>
                  <div className="text-xs text-teal-soft mt-1 flex items-center gap-2">
                    <Clock size={12} /> {s.duration_min} min
                    {s.topic_hint && <span>· {s.topic_hint}</span>}
                  </div>
                  {isBooked && (
                    <div className="text-xs text-teal-deep mt-2 bg-cream rounded-xl px-3 py-2 inline-flex items-center gap-2" data-testid={`avail-booker-${s.id}`}>
                      <User size={11} /> <span className="font-bold">{s.booked_by_name}</span>
                      {s.topic && <span className="italic text-teal-soft">— "{s.topic}"</span>}
                    </div>
                  )}
                </div>
                <button type="button" onClick={() => remove(s.id)} className="text-red-500 hover:text-red-700 text-sm inline-flex items-center gap-1" data-testid={`remove-${s.id}`}>
                  <Trash2 size={13} /> {r.removeBtn}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={close} data-testid="slot-modal">
          <div className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-6 border-b border-gray-100">
              <button type="button" onClick={close} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center" data-testid="slot-modal-close">
                <X size={16} />
              </button>
              <p className="eyebrow text-orange">{r.navLabel}</p>
              <h3 className="font-display text-2xl text-teal-deep mt-1 leading-tight">{r.addSlotBtn}</h3>
            </div>
            <form onSubmit={save} className="p-6 space-y-4" data-testid="slot-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{r.slotDate}</label>
                <input type="datetime-local" required className="field-input" min={nowLocal()} value={editing.start_at} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} data-testid="slot-start" />
                <div className="flex gap-2 mt-2 flex-wrap">
                  {[
                    { l: r.presetIn2h || "Hoy +2h", v: addHoursLocal(2) },
                    { l: r.presetTomorrow || "Mañana 9:00", v: (() => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d.toISOString().slice(0, 16); })() },
                    { l: r.presetNext3d || "En 3 días 15:00", v: (() => { const d = new Date(); d.setDate(d.getDate() + 3); d.setHours(15, 0, 0, 0); return d.toISOString().slice(0, 16); })() },
                  ].map((p) => (
                    <button key={p.l} type="button" onClick={() => setEditing({ ...editing, start_at: p.v })} className="text-xs px-2.5 py-1 rounded-md bg-cream text-teal-deep hover:bg-orange hover:text-white transition-colors border border-gray-200" data-testid={`slot-preset-${p.l}`}>
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{r.slotDuration}</label>
                <select className="field-input" value={editing.duration_min} onChange={(e) => setEditing({ ...editing, duration_min: e.target.value })} data-testid="slot-duration">
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                  <option value={90}>90 min</option>
                  <option value={120}>120 min</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-teal block mb-1">{r.slotTopicHint}</label>
                <input maxLength={120} className="field-input" placeholder={r.slotTopicHintPlaceholder} value={editing.topic_hint} onChange={(e) => setEditing({ ...editing, topic_hint: e.target.value })} data-testid="slot-topic-hint" />
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <button type="submit" disabled={saving} className="btn-orange w-full justify-center" data-testid="slot-save">
                {saving ? "…" : r.saveSlot}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default AvailabilityManager;
